import React, { useEffect, useRef, useState } from 'react'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { createQuartileTracker, fireImpressions, handleAdClick } from './adTracker'
import type { VastAd } from './types'

interface HlsAdOverlayProps {
  ad: VastAd
  onEnded: () => void
  onSkipped: () => void
}

/**
 * Full-screen overlay that plays a VAST ad video.
 *
 * Renders its own `<video>` element on top of the content player.
 * Shows an "Ad" label, skip button (after skipOffset), countdown timer,
 * and click-through "Learn more" link.
 */
export const HlsAdOverlay: React.FC<HlsAdOverlayProps> = ({ ad, onEnded, onSkipped }) => {
  const { t } = useTranslate('common')
  const adVideoRef = useRef<HTMLVideoElement>(null)
  const quartileTrackerRef = useRef(createQuartileTracker(ad))
  const impressionsFiredRef = useRef(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(ad.duration ?? 0)
  const [canSkip, setCanSkip] = useState(ad.skipOffset === null || ad.skipOffset === 0)
  const [hasStarted, setHasStarted] = useState(false)
  const progressRef = useRef<HTMLDivElement>(null)

  // Reset tracker when ad changes (e.g., ad pod with multiple ads)
  useEffect(() => {
    quartileTrackerRef.current = createQuartileTracker(ad)
    impressionsFiredRef.current = false
  }, [ad])

  // Start ad playback + rAF loop for smooth progress bar
  useEffect(() => {
    const video = adVideoRef.current
    if (!video) return

    video.play().catch((error) => {
      logger().warn('Ad video autoplay blocked, skipping ad', {}, error)
      onEnded()
    })

    let rafId: number
    const tick = () => {
      const time = video.currentTime
      const dur = video.duration

      // Update progress bar directly via DOM ref — no React re-render
      if (progressRef.current && dur && isFinite(dur) && dur > 0) {
        progressRef.current.style.width = `${(time / dur) * 100}%`
      }

      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(rafId)
  }, [onEnded])

  const handleTimeUpdate = () => {
    const video = adVideoRef.current
    if (!video) return

    setCurrentTime(video.currentTime)

    if (!impressionsFiredRef.current && video.currentTime > 0) {
      fireImpressions(ad)
      impressionsFiredRef.current = true
      setHasStarted(true)
    }

    quartileTrackerRef.current.update(video.currentTime, video.duration || duration)

    if (ad.skipOffset !== null && !canSkip && video.currentTime >= ad.skipOffset) {
      setCanSkip(true)
    }
  }

  const handleDurationChange = () => {
    const video = adVideoRef.current
    if (video && video.duration && isFinite(video.duration)) {
      setDuration(video.duration)
    }
  }

  const handleEnded = () => {
    quartileTrackerRef.current.fireComplete()
    onEnded()
  }

  const handleError = () => {
    // If the ad video fails to load, skip it — don't block content
    onEnded()
  }

  const handleSkip = () => {
    quartileTrackerRef.current.fireSkip()
    const video = adVideoRef.current
    if (video) {
      video.pause()
    }
    onSkipped()
  }

  const handleClickThrough = () => {
    const video = adVideoRef.current
    if (video) {
      video.pause()
    }
    handleAdClick(ad)
  }

  const remainingSeconds = Math.max(0, Math.ceil(duration - currentTime))
  const skipCountdownSeconds = ad.skipOffset !== null ? Math.max(0, Math.ceil(ad.skipOffset - currentTime)) : 0

  return (
    <div className="absolute inset-0 z-50 bg-black" data-testid="hls-ad-overlay">
      {/* Ad video element */}
      <video
        ref={adVideoRef}
        src={ad.mediaUrl}
        className="h-full w-full"
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
        onEnded={handleEnded}
        onError={handleError}
      />

      {/* Ad countdown — top left */}
      {hasStarted && duration > 0 && (
        <div className="absolute left-1 top-1 rounded bg-black/70 px-2 py-1 sm:left-4 sm:top-4">
          <span className="photon-caption-xs sm:photon-label-sm font-normal text-white">
            {formatRemainingTime(remainingSeconds)}
          </span>
        </div>
      )}

      {/* Click-through "Learn more" — bottom left */}
      {ad.clickThrough && (
        <div className="absolute bottom-1 left-1 sm:bottom-16 sm:left-4">
          <button
            type="button"
            onClick={handleClickThrough}
            className="rounded bg-white/20 px-2 py-1 leading-[10px] text-white transition-colors hover:bg-white/30 sm:px-5 sm:py-2.5 sm:leading-normal"
          >
            <span className="photon-caption-xs sm:photon-caption-lg">{t('adLearnMore', 'Learn more')}</span>
          </button>
        </div>
      )}

      {/* Skip / countdown — bottom right */}
      <div className="absolute bottom-1 right-1 sm:bottom-16 sm:right-4">
        {canSkip ? (
          <button
            type="button"
            onClick={handleSkip}
            className="flex items-center gap-1 rounded border border-white/50 bg-black/50 px-2 py-1 text-white transition-colors hover:bg-white/10 sm:gap-2 sm:px-5 sm:py-2.5"
          >
            <span className="photon-caption-xs sm:photon-caption-lg">{t('adSkip', 'Skip')}</span>
            <SkipAdIcon />
          </button>
        ) : (
          ad.skipOffset !== null &&
          skipCountdownSeconds > 0 && (
            <div className={'photon-caption-xs rounded bg-black/50 px-3 py-2 text-white/70'}>
              {t('adSkipIn', 'Skip in {{seconds}}s', { seconds: skipCountdownSeconds })}
            </div>
          )
        )}
      </div>

      {/* Ad progress bar — very bottom, width driven by rAF loop via ref */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <div
          ref={progressRef}
          style={{
            width: '0%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        />
      </div>
    </div>
  )
}

/** Play triangle with a vertical bar to its right. */
function SkipAdIcon() {
  return (
    <svg className="h-2 w-2 sm:h-3 sm:w-3" viewBox="0 0 12 12" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M1,1 L8,6 L1,11 Z" strokeLinejoin="round" strokeLinecap="round" stroke="white" strokeWidth="1.5" />
      <rect x="11" y="0" width="1" height="12" />
    </svg>
  )
}

function formatRemainingTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, '0')}`
  }
  return `0:${String(secs).padStart(2, '0')}`
}
